import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Heart, Target, Users, Award, ArrowLeft, 
  Shield, TrendingUp, Handshake
} from "lucide-react";
import { NestIcon } from "../components/NestIcon";

export default function AboutPage() {
  const values = [
    {
      icon: Shield,
      title: "Protection First",
      description: "We believe everyone deserves peace of mind when it comes to their home. Our mission is to protect renters from life's unexpected challenges."
    },
    {
      icon: Heart,
      title: "Customer Care",
      description: "Our members are at the heart of everything we do. We're committed to providing exceptional service and support when you need it most."
    },
    {
      icon: Handshake,
      title: "Trust & Transparency",
      description: "No hidden fees, no complicated terms. We believe in honest, straightforward communication with our members."
    },
    {
      icon: TrendingUp,
      title: "Continuous Improvement",
      description: "We're always innovating to bring you better products, better service, and better value for your money."
    }
  ];

  const stats = [
    { value: "10,000+", label: "Renters Protected" },
    { value: "$2M+", label: "Claims Paid" },
    { value: "98%", label: "Customer Satisfaction" },
    { value: "50", label: "States Covered" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <NestIcon className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Young Serif, serif' }}>therentpayer</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Young Serif, serif' }}>
            About TheRentPayer
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            We're on a mission to protect renters from financial hardship when life throws unexpected challenges. 
            Because everyone deserves the security of knowing their home is protected.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Young Serif, serif' }}>
              Our Story
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-4">
                TheRentPayer was founded with a simple belief: renters deserve the same financial protection 
                as homeowners. We saw too many people lose their homes after unexpected job loss, 
                not because they were irresponsible, but because life happened.
              </p>
              <p className="mb-4">
                Traditional insurance doesn't cover rent payments, and most renters don't have 
                months of savings to fall back on. We built TheRentPayer to bridge this gap — providing 
                affordable rent protection that gives you breathing room when you need it most.
              </p>
              <p>
                Today, we protect thousands of renters across the country, and we've paid out 
                millions in claims to help families stay in their homes during difficult times. 
                But we're just getting started.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl font-bold text-primary mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
                  {stat.value}
                </p>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ fontFamily: 'Young Serif, serif' }}>
            Our Values
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {values.map((value, idx) => (
              <Card key={idx} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <value.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{value.title}</h3>
                      <p className="text-muted-foreground">{value.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
            Ready to Protect Your Home?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Join thousands of renters who trust TheRentPayer to protect their homes. 
            Get a quote in minutes and start your coverage today.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/quote">
              <Button variant="secondary" size="lg">Get Your Quote</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="bg-transparent border-white text-white hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} TheRentPayer. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
